import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Package, Plus } from "lucide-react";
import { NewPackageDialog, PKG_STATUS_TONES, fmtMoney } from "@/pages/app/Packages";

export default function BrandPackagesTab({ brand }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get(`/workspaces/${activeWorkspaceId}/packages?brand_id=${brand.id}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { if (activeWorkspaceId) load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, brand.id]);

  return (
    <div className="space-y-3" data-testid="brand-packages-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{rows ? `${rows.length} package(s) for this brand` : "Loading…"}</span>
        <Button size="sm" className="rounded-none" onClick={() => setCreating(true)} data-testid="brand-new-package-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> New package</Button>
      </div>
      {rows && rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="brand-packages-empty">
          No packages yet. Create a monthly package to define this brand's deliverables, pricing and margin.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(rows || []).map((p) => {
          // Backend returns net_monthly_price / margin_pct ONLY when the
          // caller has package.view_rates / package.view_margins. Fields
          // being absent (not `null`) means the user isn't allowed to see
          // finance data — hide the entire chip strip so nothing leaks.
          const showPrice = p.totals && "net_monthly_price" in p.totals;
          const showMargin = p.totals && "margin_pct" in p.totals;
          return (
          <Link key={p.id} to={`/app/packages/${p.id}`} className="border border-border p-4 hover:border-primary transition-colors block" data-testid={`brand-package-card-${p.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 border border-border bg-muted/40 flex items-center justify-center"><Package className="w-4 h-4 text-muted-foreground" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{p.start_date} → {p.end_date || "∞"}</div>
              </div>
              <Badge variant="outline" className={`rounded-none text-[10px] ${PKG_STATUS_TONES[p.status] || ""}`}>{p.status}</Badge>
            </div>
            <div className="mt-2 flex gap-4 text-xs font-mono">
              {showPrice && <span className="text-emerald-700">{fmtMoney(p.totals.net_monthly_price, p.currency)}/mo</span>}
              {showMargin && <span className={p.totals.margin_pct < 0 ? "text-red-600" : "text-muted-foreground"}>margin {p.totals.margin_pct}%</span>}
              <span className="text-muted-foreground">{p.lines_count} lines</span>
              {p.totals?.monthly_hours != null && <span className="text-muted-foreground">{p.totals.monthly_hours}h/mo</span>}
            </div>
          </Link>
        );})}
      </div>
      {creating && <NewPackageDialog wsId={activeWorkspaceId} brands={[]} defaultBrandId={brand.id} onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}
